import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  MunilyAreaPictureUploaderCarouselNavigation,
  MunilyAreaPictureUploaderGallerySize,
  MUNILY_APU_GALLERY_DEFAULTS,
} from './area-picture-uploader-gallery.types';

@Component({
  selector: 'common-area-picture-uploader-gallery',
  templateUrl: './area-picture-uploader-gallery.component.html',
  styleUrls: ['./area-picture-uploader-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreaPictureUploaderGalleryComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() images: string[] = MUNILY_APU_GALLERY_DEFAULTS.images;
  /** Índice de la imagen principal (selected). */
  @Input() primaryIndex: number | null = null;
  @Input() size: MunilyAreaPictureUploaderGallerySize = MUNILY_APU_GALLERY_DEFAULTS.size;
  @Input() maxSlots: number = MUNILY_APU_GALLERY_DEFAULTS.maxSlots;
  @Input() disabled: boolean = MUNILY_APU_GALLERY_DEFAULTS.disabled;
  /** Tipos aceptados en el explorador (solo imágenes por defecto). */
  @Input() accept: string = MUNILY_APU_GALLERY_DEFAULTS.accept;
  /** Clave ngx-translate para la etiqueta visible y aria-label. */
  @Input() uploadLabelKey: string = MUNILY_APU_GALLERY_DEFAULTS.uploadLabelKey;
  /** Permite elegir varias fotos de una vez (hasta caber en slots libres). */
  @Input() multiple = true;
  /** Carril: scroll horizontal del navegador o flechas + scroll programático. */
  @Input() carouselNavigation: MunilyAreaPictureUploaderCarouselNavigation =
    MUNILY_APU_GALLERY_DEFAULTS.carouselNavigation;
  /** Reordenar miniaturas por arrastre (p. ej. galería del área en información general). */
  @Input() dragDropEnabled: boolean = MUNILY_APU_GALLERY_DEFAULTS.dragDropEnabled;

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('replaceInput') replaceInput?: ElementRef<HTMLInputElement>;
  @ViewChild('scrollTrack') scrollTrack?: ElementRef<HTMLElement>;

  /** Emite los archivos de imagen elegidos (0 si se canceló o no eran imágenes). */
  @Output() upload = new EventEmitter<File[]>();
  /** Emite el archivo elegido para reemplazar un índice existente. */
  @Output() replace = new EventEmitter<{ index: number; file: File }>();
  /** Índice eliminado (compatibilidad). El estado actualizado va en `imagesChange`. */
  @Output() remove = new EventEmitter<number>();
  /** Notifica cambio de imagen principal. */
  @Output() primaryIndexChange = new EventEmitter<number>();
  /** Emite el arreglo completo de URLs de vista previa tras añadir o quitar imágenes. */
  @Output() imagesChange = new EventEmitter<string[]>();

  emptySlots: null[] = [];
  private pendingReplaceIndex: number | null = null;

  public canScrollPrev = false;
  public canScrollNext = false;

  private readonly _scrollThresholdPx = 2;
  /** Coalesce scroll events (solo modo flechas) para no inundar CD con markForCheck. */
  private _scrollAffordanceRafId = 0;

  constructor(private readonly _cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    queueMicrotask(() => this._updateScrollAffordance());
  }

  ngOnDestroy(): void {
    if (this._scrollAffordanceRafId !== 0) {
      cancelAnimationFrame(this._scrollAffordanceRafId);
      this._scrollAffordanceRafId = 0;
    }
  }

  /** Botón de subida desactivado por `disabled` o porque ya no hay huecos libres. */
  get isUploadTriggerDisabled(): boolean {
    const count = this.images?.length ?? 0;
    return this.disabled || count >= this.maxSlots;
  }

  ngOnChanges(): void {
    const filled = Math.min(this.images?.length ?? 0, this.maxSlots);
    const empty = Math.max(0, this.maxSlots - filled);
    this.emptySlots = Array(empty).fill(null);
    queueMicrotask(() => this._updateScrollAffordance());
  }

  public onTrackScroll(): void {
    if (this.carouselNavigation !== 'arrows') {
      return;
    }
    if (this._scrollAffordanceRafId !== 0) {
      return;
    }
    this._scrollAffordanceRafId = requestAnimationFrame(() => {
      this._scrollAffordanceRafId = 0;
      this._applyScrollAffordanceFromDom();
    });
  }

  public onCarouselPrev(): void {
    if (this.carouselNavigation !== 'arrows') {
      return;
    }
    const el = this.scrollTrack?.nativeElement;
    if (!el) return;
    const delta = Math.max(120, Math.floor(el.clientWidth * 0.75));
    el.scrollBy({ left: -delta, behavior: 'smooth' });
    queueMicrotask(() => this._applyScrollAffordanceFromDom());
  }

  public onCarouselNext(): void {
    if (this.carouselNavigation !== 'arrows') {
      return;
    }
    const el = this.scrollTrack?.nativeElement;
    if (!el) return;
    const delta = Math.max(120, Math.floor(el.clientWidth * 0.75));
    el.scrollBy({ left: delta, behavior: 'smooth' });
    queueMicrotask(() => this._applyScrollAffordanceFromDom());
  }

  public onGalleryDrop(event: CdkDragDrop<string[]>): void {
    if (!this.dragDropEnabled || this.disabled) {
      return;
    }
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const next = [...(this.images ?? [])];
    moveItemInArray(next, event.previousIndex, event.currentIndex);

    const prevPrimary = this.primaryIndex;
    const nextPrimary =
      prevPrimary === null || prevPrimary === undefined
        ? null
        : this._primaryIndexAfterReorder(prevPrimary, event.previousIndex, event.currentIndex);

    this.imagesChange.emit(next);

    if (nextPrimary !== null && nextPrimary !== prevPrimary) {
      this.primaryIndexChange.emit(nextPrimary);
    }
  }

  private _primaryIndexAfterReorder(primary: number, from: number, to: number): number {
    if (primary === from) {
      return to;
    }
    if (from < to) {
      if (primary > from && primary <= to) {
        return primary - 1;
      }
    } else if (from > to) {
      if (primary >= to && primary < from) {
        return primary + 1;
      }
    }
    return primary;
  }

  private _updateScrollAffordance(): void {
    if (this.carouselNavigation !== 'arrows') {
      this._setAffordanceBooleans(false, false);
      return;
    }
    this._applyScrollAffordanceFromDom();
  }

  private _applyScrollAffordanceFromDom(): void {
    const el = this.scrollTrack?.nativeElement;
    if (!el) {
      this._setAffordanceBooleans(false, false);
      return;
    }

    const { scrollLeft, clientWidth, scrollWidth } = el;
    const t = this._scrollThresholdPx;
    const nextPrev = scrollLeft > t;
    const nextNext = scrollLeft + clientWidth < scrollWidth - t;
    this._setAffordanceBooleans(nextPrev, nextNext);
  }

  private _setAffordanceBooleans(nextPrev: boolean, nextNext: boolean): void {
    if (nextPrev === this.canScrollPrev && nextNext === this.canScrollNext) {
      return;
    }
    this.canScrollPrev = nextPrev;
    this.canScrollNext = nextNext;
    this._cdr.markForCheck();
  }

  onUploadClick(): void {
    if (this.isUploadTriggerDisabled) {
      return;
    }
    this.fileInput?.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.files ? Array.from(input.files) : [];
    input.value = '';
    const remaining = Math.max(0, this.maxSlots - (this.images?.length ?? 0));
    if (remaining <= 0 || raw.length === 0) {
      return;
    }
    const imageFiles = raw.filter((f) => f.type.startsWith('image/')).slice(0, remaining);
    if (imageFiles.length === 0) {
      return;
    }
    const newUrls = imageFiles.map((f) => URL.createObjectURL(f));
    const current = [...(this.images ?? [])];
    const next = [...current, ...newUrls].slice(0, this.maxSlots);
    this.imagesChange.emit(next);
    this.upload.emit(imageFiles);
  }

  onReplaceFileSelected(event: Event): void {
    const index = this.pendingReplaceIndex;
    this.pendingReplaceIndex = null;

    const input = event.target as HTMLInputElement;
    const raw = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (index == null || index < 0 || index >= (this.images?.length ?? 0)) return;
    const file = raw.find((f) => f.type.startsWith('image/'));
    if (!file) return;

    const prevUrl = this.images[index];
    if (prevUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(prevUrl);
    }

    const nextUrl = URL.createObjectURL(file);
    const next = [...this.images];
    next[index] = nextUrl;
    this.imagesChange.emit(next);
    this.replace.emit({ index, file });
  }

  onRemove(index: number): void {
    if (this.disabled) {
      return;
    }
    const removed = this.images[index];
    if (removed?.startsWith('blob:')) {
      URL.revokeObjectURL(removed);
    }
    const next = this.images.filter((_, i) => i !== index);
    this.imagesChange.emit(next);
    this.remove.emit(index);
  }

  onItemMenuAction(index: number, action: 'replace' | 'setPrimary' | 'delete'): void {
    if (this.disabled) return;

    if (action === 'delete') {
      this.onRemove(index);
      return;
    }

    if (action === 'setPrimary') {
      this.primaryIndex = index;
      this.primaryIndexChange.emit(index);
      return;
    }

    // replace
    this.pendingReplaceIndex = index;
    this.replaceInput?.nativeElement.click();
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByImageUrl(_index: number, url: string): string {
    return url;
  }
}
